<template>
    <psi-form-layout-section
        title="Program Sessions"
        :icon="icon"
        @section-save="save"
        :loading="sectionLoading.sessions"
    >
        <div class="d-flex">
            <v-chip color="primary lighten-3 primary--text  text-uppercase"
                >{{ sessions.length }} Sessions</v-chip
            >
            <v-spacer></v-spacer>
            <v-btn-toggle
                v-model="toggle"
                mandatory
                color="primary darken-2"
                ref="btn-toggle"
            >
                <v-btn>
                    Edit Session
                    <v-icon class="ml-2">mdi-calendar-edit</v-icon>
                </v-btn>
                <v-btn>
                    Add Session
                    <v-icon class="ml-2">mdi-calendar-plus</v-icon>
                </v-btn>
            </v-btn-toggle>
        </div>
        <v-slide-x-transition>
            <psi-form-element
                label="Select Session to Edit"
                v-if="toggle === 0"
            >
                <psi-form-select-field
                    :items="sessionsData"
                    id="select-session"
                    name="session"
                    :value="0"
                    item-text="name"
                    item-value="id"
                    placeholder="Select Session"
                    return-object
                    @input="selectSession"
                >
                </psi-form-select-field>
            </psi-form-element>
        </v-slide-x-transition>
        <v-slide-x-reverse-transition group>
            <psi-form-section
                :key="toggle"
                v-if="Object.keys(session).length > 0"
                ref="session-update"
                name="input-session-update"
                :value="session"
                label=""
                :fields="fields"
                color="secondary"
            ></psi-form-section>
            <div
                :key="`instructor-${toggle}`"
                v-if="
                    Object.keys(session).length > 0 &&
                    session.instructors.length > 0
                "
            >
                <h4 class="font-weight-regular mb-2">Session Instructors</h4>
                <instructor-select-list
                    :items="session.instructors"
                    @select-instructor="selectInstructor"
                ></instructor-select-list>
            </div>
        </v-slide-x-reverse-transition>

        <template #actions>
            <v-btn
                color="accent darken-2"
                :disabled="disabled || removeDisabled"
            >
                <v-icon class="mr-2">mdi-calendar-remove</v-icon>
                Remove Session
            </v-btn>
            <v-btn
                color="primary darken-2"
                @click.stop="save"
                :disabled="disabled"
            >
                <v-icon class="mr-2">{{ icon }}</v-icon>
                {{ label }}
            </v-btn>
        </template>
    </psi-form-layout-section>
</template>
<script>
const MODE_EDITING = 0, // NOTE: affected by button position in the button group
    MODE_ADDING = 1;

export default {
    name: "program-sessions",
    components: {
        InstructorSelectList: () =>
            import("../ProgramEdit/InstructorsSelectList.vue"),
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        program: {
            type: Object,
            required: true,
        },
        sessions: {
            type: Array,
            required: true,
        },
        instructors: {
            type: Array,
            required: true,
        },
        sectionLoading: {
            type: Object,
            required: false,
            default: () => {
                return {
                    sessions: false,
                };
            },
        },
        query: {
            type: Object,
            required: false,
            default: () => {
                return {};
            },
        },
    },
    data() {
        return {
            toggle:
                this.sessions.length > 0 &&
                !this.$_.get(this.query, "addsession", false)
                    ? MODE_EDITING
                    : MODE_ADDING, // we'l start with edit if we have existing sessions
            session: {},
            sessionsData: [],
            fields: [
                {
                    type: "text",
                    name: "name",
                    key: "name",
                    label: "Name",
                    icon: this.$config("icons.program"),
                    required: true,
                },
                {
                    type: "date",
                    name: "start_date",
                    key: "start_date",
                    label: "Start Date",
                    icon: this.$config("icons.start_date"),
                    required: true,
                },
                {
                    type: "date",
                    name: "end_date",
                    key: "end_date",
                    label: "End Date",
                    icon: this.$config("icons.end_date"),
                    required: true,
                },
            ],
        };
    },
    computed: {
        disabled() {
            return (
                this.$_.get(this.program, "id", 0) === 0 ||
                Object.keys(this.session).length === 0
            );
        },
        removeDisabled() {
            const hasInstructors =
                this.$_.get(this.session, "instructors", []).filter(
                    (item) => item.hasSession
                ).length > 0;
            const hasDatabaseRecord = this.$_.has(this.session, "id");
            const isValid =
                this.session !== null &&
                this.session !== undefined &&
                Object.keys(this.session).length > 0;

            // Disabled if we still have instructors or we don't have a DB record or the session isn't valid
            return hasInstructors || !hasDatabaseRecord || !isValid;
        },
        icon() {
            return this.$config("icons.session");
        },
        label() {
            return `${this.mode} Session`;
        },
        mode() {
            return this.toggle === MODE_EDITING ? "Update" : "Add";
        },
    },
    watch: {
        toggle: {
            immediate: true,
            handler(toggle) {
                if (toggle === MODE_ADDING) {
                    this.session = this.initialize();
                }
                // resetting on toggle
                if (toggle === MODE_EDITING) {
                    this.session = {};
                }
            },
        },
        sessions: {
            immediate: true,
            handler(sessions) {
                // add all the instructors to the session but mapped by Has Session
                this.sessionsData = sessions.map((session) => {
                    let { start_date, end_date } = session;
                    session.instructors = this.sessionInstructors(
                        session.instructors,
                        this.instructors
                    );
                    return {
                        ...session,
                        start_date: this.$dateFormat(start_date),
                        end_date: this.$dateFormat(end_date),
                    };
                });
            },
        },
    },
    methods: {
        sessionInstructors(sourceColl, targetColl) {
            return targetColl.map((item) => {
                const srcIndex = sourceColl.findIndex(
                    (src) => src.id === item.id
                );
                return {
                    hasSession: srcIndex !== -1,
                    students_count:
                        srcIndex === -1
                            ? 0
                            : this.$_.get(
                                  sourceColl[srcIndex],
                                  "students_count",
                                  0
                              ),
                    ...item,
                };
            });
        },

        selectSession(session) {
            this.session = Object.assign({}, session);
        },
        initialize() {
            return {
                instructors: this.sessionInstructors([], this.instructors),
                ...this.fields.reduce((acc, curr) => {
                    acc[curr.key] = "";
                    return acc;
                }, {}),
            };
        },
        selectInstructor(instructors) {
            this.session = Object.assign(this.session, {
                instructors: this.session.instructors.map((item) => {
                    item.hasSession =
                        instructors.findIndex((ins) => ins.id === item.id) !==
                        -1;
                    return item;
                }),
            });
        },

        save() {
            if (this.$refs["session-update"].validate()) {
                const payload = {
                    program_id: this.program.id,
                    meta: {
                        mode: this.mode,
                    },
                    ...this.session,
                    instructors: this.session.instructors
                        .filter((item) => item.hasSession)
                        .map((item) => item.id),
                };
                this.$emit("update", this.id, payload);
            }
        },
    },
    mounted() {
        this.$emit("mounted", this.id);
    },
};
</script>

<style scoped>
</style>